import { Inject, Injectable } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';

/** Storage service used for persist application data in observable key value pair */
export class StorageService {
	private storage: Storage;
	private subjects: Map<string, ReplaySubject<any>>;
	private watched = {};

	/**
	 * Constructor with service injection
	 *
	 * @param storage
	 */
	constructor(storage: Storage) {
		this.storage = storage;
		this.subjects = new Map<string, ReplaySubject<any>>();
	}

	/**
	 * Watch data of given key
	 *
	 * @param key
	 */
	watch(key: string): Observable<any> {
		if (!this.subjects.has(key)) {
			this.subjects.set(key, new ReplaySubject<any>(1));
		}
		let item = this.storage.getItem(key);
		if (item === undefined) {
			item = undefined;
		} else if (item === null) {
			item = null;
		} else {
			if (!this.watched[key]) {
				// item = JSON.parse(item);
				this.subjects.get(key).next(item);
				this.watched[key] = true;
			}
		}
		return this.subjects.get(key).asObservable();
	}

	/**
	 * Get data of given key
	 *
	 * @param key
	 */
	get(key: string): string | null {
		const item = this.storage.getItem(key);
		return item;
	}

	/**
	 * Set value on given key
	 *
	 * @param key
	 * @param value
	 */
	set(key: string, value: any) {
		this.storage.setItem(key, value);
		if (!this.subjects.has(key)) {
			this.subjects.set(key, new ReplaySubject<any>(1));
		}
		if (!this.watched[key]) {
			this.watched[key] = true;
		}
		this.subjects.get(key).next(value);
	}

	/**
	 * Remove given key
	 *
	 * @param key
	 */
	remove(key: string) {
		if (this.subjects.has(key)) {
			this.subjects.get(key).next(null);
			// this.subjects.get(key).complete();
			// this.subjects.delete(key);
		}
		this.storage.removeItem(key);
	}

	/** Clear all available keys */
	clear() {
		this.subjects.forEach((subj) => {
			subj.next(null);
		});
		this.storage.clear();
	}

	/*
    Implement standard browser storage interface
    Necessary to make the class compatible with amazon-cognito-identity-js - search for i.e.:
    let poolData = {
      Storage: window.sessionStorage, <--- here - this was window.localStorage per default
  */
	getItem(key) {
		return this.get(key);
	}

	setItem(key, value) {
		this.set(key, value);
	}

	removeItem(key) {
		this.remove(key);
	}
}

/** Session storage service - to be able to run different accounts in separate browser tabs */
@Injectable({
	providedIn: 'root',
})
export class SessionStorageService extends StorageService {
	/**
	 * Constructor with service injection
	 *
	 * @param window
	 */
	constructor(@Inject('WINDOW') private window: any) {
		super(window.sessionStorage);
	}
}

/** Local storage service used for persist application data in observable key value pair */
@Injectable({
	providedIn: 'root',
})
export class LocalStorageService extends StorageService {
	/**
	 * Constructor with service injection
	 *
	 * @param window
	 */
	constructor(@Inject('WINDOW') private window: any) {
		super(window.localStorage);
	}
}
